import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import MotionTween from 'motion-tween';
import useViewUpdate from '../../../managers/hooks/useViewUpdate';

const Accordion = ({
  drawerElement,
  isOpen,
  onChange,
  toggleElement,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(isOpen);
  const drawerRef = useRef();
  const motionTweenRef = useRef();
  const previousIsOpen = useRef(!isOpen);

  function setFinalDrawerHeight(isFinalDrawerOpen) {
    if (isFinalDrawerOpen) {
      drawerRef.current.style.height = 'auto';
    } else {
      drawerRef.current.style.height = '0px';
    }
  }

  function destroyMotionTween() {
    if (motionTweenRef.current) {
      motionTweenRef.current.destroy();
      motionTweenRef.current = null;
    }
  }

  function onUpdateAnimateItems(value, start, end) {
    if (drawerRef.current) {
      const px = start + ((end - start) * value);
      drawerRef.current.style.height = `${px}px`;
    }
  }

  function onUpdateAnimateItemsComplete(isFinalDrawerOpen) {
    destroyMotionTween();
    setFinalDrawerHeight(isFinalDrawerOpen);
  }

  useViewUpdate((event) => {
    if (event.resizeUpdate) {
      destroyMotionTween();
    }
  }, [isDrawerOpen, drawerRef, motionTweenRef]);

  useEffect(() => {
    drawerRef.current.style.willChange = 'height';
    drawerRef.current.style.overflow = 'hidden';

    setFinalDrawerHeight(isDrawerOpen);
  }, []);

  useEffect(() => {
    if (onChange) onChange(isDrawerOpen);
  }, [isDrawerOpen]);


  function onClose() {
    if (drawerRef.current) {
      setIsDrawerOpen(false);
      const start = drawerRef.current.offsetHeight;
      const end = 0;

      destroyMotionTween();

      motionTweenRef.current = new MotionTween({
        animatorType: MotionTween.animatorType.springRK4,
        animatorOptions: {
          stiffness: 500,
          damping: 50,
        },
        update: value => onUpdateAnimateItems(value, start, end),
        complete: () => onUpdateAnimateItemsComplete(false),
      }).start();
    }
  }

  function onOpen() {
    if (drawerRef.current) {
      setIsDrawerOpen(true);
      const start = drawerRef.current.offsetHeight;
      drawerRef.current.style.height = 'auto';
      window.getComputedStyle(drawerRef.current);
      const end = drawerRef.current.offsetHeight;
      drawerRef.current.style.height = `${start}px`;

      destroyMotionTween();

      motionTweenRef.current = new MotionTween({
        animatorType: MotionTween.animatorType.springRK4,
        animatorOptions: {
          stiffness: 225,
          damping: 22.5,
        },
        update: value => onUpdateAnimateItems(value, start, end),
        complete: () => onUpdateAnimateItemsComplete(true),
      }).start();
    }
  }

  if (previousIsOpen.current !== isOpen) {
    if (isOpen) {
      onOpen();
    } else {
      onClose();
    }
    previousIsOpen.current = isOpen;
  }

  return (
    <>
      {toggleElement({ onOpen, onClose, isOpen: isDrawerOpen })}
      {drawerElement({
        onOpen,
        onClose,
        drawerRef,
        isOpen:
        isDrawerOpen,
      })}
    </>
  );
};

Accordion.propTypes = {
  drawerElement: PropTypes.func.isRequired,
  isOpen: PropTypes.bool,
  toggleElement: PropTypes.func.isRequired,
  onChange: PropTypes.func,
};

export default Accordion;
